import { Component, ContentChildren, ElementRef, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChild } from '@angular/core';
import { StepChildrenComponent } from 'src/app/shared/components/step-children-component';
import { VerticalTabContentDirective } from './vertical-tab-content.directive';

export interface VerticalTabItem {
	id: string;
	label: string;
	icon?: string;
	invalid?: boolean;
}

export interface VerticalTabChangeEvent {
	previousIndex: number;
	currentIndex: number;
}

@Component({
	selector: 'app-vertical-tabs',
	templateUrl: './vertical-tabs.component.html',
	styleUrls: ['./vertical-tabs.component.scss']
})
export class VerticalTabsComponent implements OnChanges, StepChildrenComponent {
	@Input() tabs: VerticalTabItem[] = [];
	@ContentChildren(VerticalTabContentDirective) tabContents!: QueryList<VerticalTabContentDirective>;
	@Output() tabChanged = new EventEmitter<VerticalTabChangeEvent>();
	@ViewChild('tabBody') tabBody!: ElementRef<HTMLDivElement>;
	selectedTab: string = '';

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['tabs'] && !this.tabs.some(tab => tab.id === this.selectedTab)) {
			this.selectedTab = this.tabs.length > 0 ? this.tabs[0].id : '';
		}
	}

	selectTab(tabId: string): void {
		// Get the index of the tab that's losing focus (current)
		const previousIndex = this.tabs.findIndex(tab => tab.id === this.selectedTab);
		// Get the index of the new tab that's gaining focus
		const currentIndex = this.tabs.findIndex(tab => tab.id === tabId);
		
		this.selectedTab = tabId;
		
		// Emit both the previous and current indices
		this.tabChanged.emit({ previousIndex, currentIndex });
	}

	getSelectedTabLabel(): string {
		const selectedTab = this.tabs.find(tab => tab.id === this.selectedTab);
		return selectedTab ? selectedTab.label : '';
	}

	get hasPrevious(): boolean {
		const currentIndex = this.tabs.findIndex(tab => tab.id === this.selectedTab);
		return currentIndex > 0;
	}

	get hasNext(): boolean {
		const currentIndex = this.tabs.findIndex(tab => tab.id === this.selectedTab);
		return currentIndex >= 0 && currentIndex < this.tabs.length - 1;
	}

	get isLastIndex(): boolean {
		const currentIndex = this.tabs.findIndex(tab => tab.id === this.selectedTab);
		return currentIndex === this.tabs.length - 1;
	}

	get isFirstIndex(): boolean {
		const currentIndex = this.tabs.findIndex(tab => tab.id === this.selectedTab);
		return currentIndex === 0;
	}

	onNext(): void {
		const currentIndex = this.tabs.findIndex(tab => tab.id === this.selectedTab);
		if (currentIndex >= 0 && currentIndex < this.tabs.length - 1) {
			this.selectTab(this.tabs[currentIndex + 1].id);
		}
	}

	onPrevious(): void {
		const currentIndex = this.tabs.findIndex(tab => tab.id === this.selectedTab);
		if (currentIndex > 0) {
			this.selectTab(this.tabs[currentIndex - 1].id);
		}
	}

	scrollToTop(): void {
		if (this.tabBody) {
			this.tabBody.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
		}
		// También hacer scroll en elementos padres que tengan overflow
		let parent = this.tabBody?.nativeElement?.parentElement;
		while (parent) {
			const computedStyle = window.getComputedStyle(parent);
			if (computedStyle.overflowY === 'auto' || computedStyle.overflowY === 'scroll') {
				parent.scrollTo({ top: 0, behavior: 'smooth' });
			}
			parent = parent.parentElement;
		}
	}
}